import { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";

/**
 * Public landing page.
 *
 * Cinematic, dark, big typography — designed to load instantly and never
 * touch the backend so it works even when the API is down.  All content is
 * static.  The only interactive elements are anchor scroll links and the
 * "Sign in" button which deep-links to /login.
 */
export default function Landing() {
  const [scrolled, setScrolled] = useState(false);
  const ctaRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Hidden until the business is ready for self-serve.  Owner accesses the
  // workspace directly via /login (bookmark) — see comment in App.js.
  // const goSignIn = () => { ... }

  const CONTACT_EMAIL = "iainhmunro@gmail.com";
  const SUBJECT = "?subject=" + encodeURIComponent("Get a Reel");
  const mailtoQuote = `mailto:${CONTACT_EMAIL}${SUBJECT}`;
  const mailtoDemo = `mailto:${CONTACT_EMAIL}${SUBJECT}`;

  return (
    <div className="min-h-screen bg-[#050505] text-white grain overflow-x-hidden">
      {/* ── Top nav ─────────────────────────────────────────────────── */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all ${
          scrolled
            ? "bg-[#050505]/85 backdrop-blur-xl border-b border-white/5"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-4 flex items-center gap-6">
          <a href="#top" className="flex items-center gap-2 group" data-testid="landing-logo">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center shadow-[0_0_24px_rgba(251,191,36,0.25)]">
              <Icons.Film size={14} className="text-black" />
            </div>
            <div className="font-display text-base font-medium tracking-tight">
              Property Motion <span className="text-zinc-500">AI</span>
            </div>
          </a>
          <div className="hidden md:flex items-center gap-7 text-sm text-zinc-400 ml-6">
            <a href="#how" className="hover:text-white transition-colors">How it works</a>
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#examples" className="hover:text-white transition-colors">Examples</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
          </div>
          <div className="flex-1" />
          <a
            href={mailtoQuote}
            className="hidden md:inline-flex items-center text-sm text-zinc-400 hover:text-white transition-colors"
            data-testid="landing-contact"
          >
            Contact
          </a>
          <a
            href={mailtoQuote}
            className="text-sm font-medium bg-white text-black rounded-full px-5 py-2 hover:bg-zinc-200 active:scale-[0.97] transition-all"
            data-testid="landing-cta-nav"
          >
            Get a reel
          </a>
        </div>
      </nav>

      {/* ── Hero ──────────────────────────────────────────────────── */}
      <section id="top" className="relative pt-36 lg:pt-48 pb-32 px-6 lg:px-12">
        {/* Background glow */}
        <div
          className="absolute inset-0 opacity-50 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 70% 20%, rgba(251,191,36,0.08), transparent 50%), radial-gradient(circle at 20% 80%, rgba(56,189,248,0.06), transparent 50%)",
          }}
        />
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
              backgroundSize: "80px 80px",
            }}
          />
        </div>

        <div className="relative max-w-[1280px] mx-auto">
          <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 text-[11px] tracking-[0.2em] uppercase text-zinc-300 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Now with realtor headshots, open-house overlays & QR-coded flyers
          </div>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-medium tracking-[-0.02em] leading-[0.95] mb-8">
            Cinematic listing reels.
            <br />
            <span className="text-zinc-500">Made from photos.</span>
            <br />
            <span className="text-amber-300">In minutes.</span>
          </h1>

          <p className="text-lg lg:text-xl text-zinc-400 max-w-2xl leading-relaxed mb-12">
            Send me the MLS listing and a few notes on the look you want.
            Within 48 hours you get a finished cinematic reel — camera moves,
            transitions, voiceover, branded intro & outro cards, plus a
            print-ready flyer with a QR straight to the video.
          </p>

          <div className="flex flex-wrap items-center gap-3 mb-20">
            <a
              href={mailtoQuote}
              className="inline-flex items-center gap-2 bg-white text-black font-medium text-base rounded-full px-7 py-3.5 hover:bg-zinc-200 active:scale-[0.97] transition-all shadow-[0_8px_40px_rgba(255,255,255,0.15)]"
              data-testid="landing-hero-cta"
            >
              <Icons.Mail size={16} />
              Get a reel made
            </a>
            <a
              href="#examples"
              className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-6 py-3.5 transition-all"
              data-testid="landing-see-examples"
            >
              <Icons.Sparkles size={16} />
              See examples
            </a>
          </div>

          {/* Marquee-style tile preview */}
          <div className="relative">
            <div className="aspect-[16/8] rounded-3xl overflow-hidden border border-white/10 bg-black shadow-[0_60px_180px_-30px_rgba(0,0,0,0.9)]">
              <div className="grid grid-cols-3 h-full">
                {[
                  "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=1200&q=80",
                  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80",
                  "https://images.unsplash.com/photo-1600585154526-990dced4db0d?auto=format&fit=crop&w=1200&q=80",
                ].map((src, i) => (
                  <div key={i} className="relative overflow-hidden border-r border-black last:border-r-0">
                    <img
                      src={src}
                      alt=""
                      className="w-full h-full object-cover"
                      style={{
                        animation: `subtleZoom 18s ${i * 6}s ease-in-out infinite alternate`,
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  </div>
                ))}
              </div>
              {/* Caption overlay */}
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-amber-300 mb-2">
                    Presented by
                  </div>
                  <div className="font-display text-2xl lg:text-3xl">125 Saddlefield Crescent NE</div>
                </div>
                <div className="hidden lg:flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/15 rounded-full px-4 py-2 text-xs">
                  <Icons.Wand2 size={12} className="text-violet-300" />
                  <span>Crossfade · 0.6 s</span>
                </div>
              </div>
            </div>
            <style>{`@keyframes subtleZoom {0%{transform:scale(1)}100%{transform:scale(1.08)}}`}</style>
          </div>
        </div>
      </section>

      {/* ── Logo strip / social proof ─────────────────────────────── */}
      <section className="border-y border-white/5 py-10 px-6 lg:px-12">
        <div className="max-w-[1280px] mx-auto flex flex-col lg:flex-row items-center gap-8 justify-between">
          <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-500">
            Trusted by realtors generating
          </div>
          <div className="grid grid-cols-3 lg:grid-cols-6 gap-x-12 gap-y-4 text-zinc-300 font-mono text-sm">
            <Stat value="600+" label="reels" />
            <Stat value="9" label="transitions" />
            <Stat value="11" label="languages" />
            <Stat value="1080p" label="cinematic" />
            <Stat value="A4" label="print-ready" />
            <Stat value="∞" label="iterations" />
          </div>
        </div>
      </section>

      {/* ── How it works ──────────────────────────────────────────── */}
      <section id="how" className="py-32 px-6 lg:px-12">
        <div className="max-w-[1280px] mx-auto">
          <SectionHeader
            eyebrow="HOW IT WORKS"
            title="Three steps. I do the rest."
            sub="No drone hire. No scriptwriter. No Adobe Premiere. Just a quick email — and a finished reel back."
          />
          <div className="grid md:grid-cols-3 gap-6 mt-16">
            <Step
              n={1}
              icon="Mail"
              title="Send me the MLS link"
              body="Email me the MLS listing, how many photos you want in the reel, and any style notes for each (modern, luxury, cosy, family, twilight reveal — whatever fits the property)."
              accent="from-amber-300 to-amber-500"
            />
            <Step
              n={2}
              icon="Wand2"
              title="I do the work"
              body="I pull the photos, run them through Property Motion AI — camera moves, transitions, voiceover, branded intro & outro cards, virtual staging or twilight reveals if needed."
              accent="from-violet-300 to-fuchsia-500"
            />
            <Step
              n={3}
              icon="Send"
              title="You get the reel, flyer & your own dashboard"
              body="Within 48 hours: a finished cinematic reel, social cuts (9:16, 1:1, 16:9), a printable A4 PDF flyer with a QR code — plus your own private Realtor Dashboard tracking views, drop-off, geo-map of every viewer's city, and which listings are actually converting. (Video walkthrough coming soon — it's a game-changer.)"
              accent="from-sky-300 to-emerald-400"
            />
          </div>
        </div>
      </section>

      {/* ── Feature grid ──────────────────────────────────────────── */}
      <section id="features" className="py-32 px-6 lg:px-12 bg-gradient-to-b from-transparent via-white/[0.015] to-transparent">
        <div className="max-w-[1280px] mx-auto">
          <SectionHeader
            eyebrow="FEATURES"
            title="Every tool you'd hire a videographer for."
            sub="Built into one screen."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mt-16">
            <Feature
              icon="Sparkles"
              title="Smart Reel"
              body="One click. AI picks the photos, assigns camera moves, queues clips, and stitches the reel — including intro & outro cards."
            />
            <Feature
              icon="Sofa"
              title="Virtual Staging"
              body="Empty rooms turned into modern, luxury, family, or cosy interiors. AI repaints — pixel-perfect lighting preserved."
            />
            <Feature
              icon="Sunrise"
              title="Day → Night Reveal"
              body="Show a property at golden-hour, then dissolve into a twilight version of the same shot. Magnificent for hero exteriors."
            />
            <Feature
              icon="Wand2"
              title="Empty → Furnished Reveal"
              body="Same cinematic crossfade as Day→Night, but for empty rooms — bare space dissolves into a fully staged interior, in a single shot."
            />
            <Feature
              icon="Wand2"
              title="9 Cinematic Transitions"
              body="Crossfade, dip-to-black, slide, whip-pan, zoom-blur, mixed — pick a style or let it auto-rotate for variety."
            />
            <Feature
              icon="Layers"
              title="Persistent Overlays"
              body="Brokerage logo, realtor headshot, big bold title text, full-width Open House banner — composed live, time-ranged."
            />
            <Feature
              icon="LineChart"
              title="Realtor Dashboard (game-changer)"
              body="Your own private, brand-themed dashboard. Live view counts, watch-time drop-off per reel, geo-map of every viewer's city, last-watched timestamps, and which listings are actually converting. Auto-refreshes every 30s — share the link with your broker or your seller."
            />
            <Feature
              icon="Printer"
              title="Print-ready PDF Flyer"
              body="One click → A4 flyer with cover photo, headshot, address, price, and a QR code that goes straight to the reel."
            />
            <Feature
              icon="Mic2"
              title="ElevenLabs Voiceover"
              body="Choose a voice, write a script (or auto-generate one), and the reel gets a polished narration in seconds."
            />
            <Feature
              icon="Tag"
              title="Listing Stats Cards"
              body="Branded title cards with price, beds, baths, sqft. Multi-currency, metric or imperial — your colors, your fonts."
            />
          </div>
        </div>
      </section>

      {/* ── Before / After examples ───────────────────────────────── */}
      <section id="examples" className="py-32 px-6 lg:px-12">
        <div className="max-w-[1280px] mx-auto">
          <SectionHeader
            eyebrow="EXAMPLES"
            title="A single photo. Endless transformations."
            sub="Every image below started as one ordinary listing photo. Hover or tap to see the AI transformation — the same engine we use on every reel."
          />
          <div className="grid md:grid-cols-2 gap-6 mt-16">
            <BeforeAfter
              label="Day → Twilight reveal"
              caption="A real reel I made for one of my listings — front exterior repainted into twilight, then crossfaded so it opens with a cinematic time-lapse without booking a second shoot."
              video="/demo/twilight.mp4"
              accent="from-amber-300 to-orange-500"
              icon="Sunset"
            />
            <BeforeAfter
              label="Empty → Furnished reveal"
              caption="Same cinematic crossfade as the twilight reveal — but for empty rooms. AI furnishes the space, then dissolves the bare room into the staged interior in a single shot. Choose modern, luxury, cozy, scandi, or farmhouse."
              video="/demo/staged.mp4"
              accent="from-violet-300 to-fuchsia-500"
              icon="Sofa"
            />
            <BeforeAfter
              label="Empty bedroom → cosy"
              caption="Bare master suites turned into magazine-worthy bedrooms. Lighting, floor, and window stay intact — the AI just adds furniture."
              before="https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=1200&q=80"
              after="https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=1200&q=80"
              accent="from-sky-300 to-blue-500"
              icon="Bed"
            />
            <BeforeAfter
              label="Drone Orbit (no drone required)"
              caption="One still drone photo → AI-synthesized 360° orbit around the property. No second flight, no pilot, no insurance. The original was a single overhead frame; everything you see moving is generated."
              video="/demo/drone-orbit.mp4"
              accent="from-emerald-300 to-teal-500"
              icon="Plane"
            />
          </div>
        </div>
      </section>

      {/* ── Sample reel ───────────────────────────────────────────── */}
      <section id="sample" className="py-24 px-6 lg:px-12">
        <div className="max-w-[1280px] mx-auto">
          <SectionHeader
            eyebrow="THE FINAL REEL"
            title="Stitched. Voiced. Branded."
            sub="Every transformation above is just one frame — strung together with transitions, voiceover, branded cards, and overlays into a 30-60 second cinematic reel."
          />
          <div className="mt-16 grid lg:grid-cols-[1fr_400px] gap-8 items-center">
            <div className="aspect-video rounded-2xl overflow-hidden border border-white/10 bg-black relative shadow-[0_40px_120px_-20px_rgba(0,0,0,0.8)]">
              <img
                src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1600&q=80"
                alt="Sample listing"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <a
                  href={mailtoDemo}
                  className="w-20 h-20 rounded-full bg-white/95 hover:bg-white text-black flex items-center justify-center shadow-2xl active:scale-95 transition-all group"
                  data-testid="landing-sample-play"
                >
                  <Icons.Play size={28} className="fill-black ml-1.5 group-hover:scale-110 transition-transform" />
                </a>
              </div>
              {/* Open House overlay (mock) */}
              <div className="absolute bottom-0 left-0 right-0 bg-[#0B1A2D]/95 px-6 py-4 flex items-center justify-between">
                <div>
                  <div className="font-display text-lg font-medium">OPEN HOUSE</div>
                </div>
                <div className="text-right text-sm text-zinc-300">
                  <div>Sat 8 Oct · 2 – 4 PM</div>
                  <div className="text-xs text-zinc-500">125 Saddlefield Crescent NE</div>
                </div>
              </div>
              {/* Headshot overlay (mock) */}
              <div className="absolute bottom-24 right-6 w-16 h-16 rounded-lg overflow-hidden border-2 border-amber-300/80 shadow-xl">
                <div className="w-full h-full bg-zinc-700 flex items-center justify-center">
                  <Icons.User size={20} className="text-zinc-500" />
                </div>
              </div>
            </div>
            <div className="space-y-5">
              <SampleStep idx="01" t="You email me the MLS link" sub="+ how many photos · any style notes" done />
              <SampleStep idx="02" t="I pull photos & queue Smart Reel" sub="Magnific Seedance · 1080p" />
              <SampleStep idx="03" t="Voice + transitions added" sub="ElevenLabs · Crossfade" />
              <SampleStep idx="04" t="Stitch + overlays" sub="ffmpeg · 18 s" />
              <SampleStep idx="05" t="You get a share link + flyer" sub="QR-coded A4 PDF · within 48 h" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Why us ─────────────────────────────────────────────────── */}
      <section className="py-32 px-6 lg:px-12">
        <div className="max-w-[1280px] mx-auto grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-5">WHY PROPERTY MOTION AI</div>
            <h2 className="font-display text-4xl lg:text-5xl leading-[1.1] mb-6">
              Other tools spit out reels.
              <br />
              <span className="text-amber-300">We close the loop.</span>
            </h2>
            <p className="text-lg text-zinc-400 leading-relaxed">
              Most AI video tools stop at "here's an mp4." We follow the
              listing all the way through — branded reels, social cuts,
              printable flyers, view-analytics dashboards, and QR codes that
              connect physical signage back to digital tracking.
            </p>
          </div>
          <div className="space-y-4">
            <Compare line="Branded intro & outro cards baked in" us />
            <Compare line="Persistent overlays — logo, headshot, open house banner" us />
            <Compare line="Per-realtor dashboards with view + drop-off analytics" us />
            <Compare line="A4 PDF flyer with QR code linking back to the reel" us />
            <Compare line="Day → Night exterior reveals" us />
            <Compare line="Virtual staging on empty rooms" us />
            <Compare line="9-style transition picker with auto-mix" us />
            <Compare line="Multi-currency & metric/imperial stats cards" us />
          </div>
        </div>
      </section>

      {/* ── Pricing tease ───────────────────────────────────────── */}
      <section id="pricing" className="py-32 px-6 lg:px-12">
        <div className="max-w-[1280px] mx-auto">
          <SectionHeader
            eyebrow="PRICING"
            title="One realtor, one team, one brokerage — same pricing model."
            sub="Pay per reel or go unlimited. No drone hire. No videographer."
          />
          <div className="grid md:grid-cols-3 gap-4 mt-16">
            <PricingCard
              name="Single reel"
              price="From £149"
              cadence="per listing · pay-as-you-go"
              features={[
                "Cinematic 30-60 s reel",
                "Branded intro & outro cards",
                "Public share page with view tracking",
                "9:16 + 1:1 + 16:9 social cuts",
              ]}
              cta="Email me"
              onClick={() => (window.location.href = mailtoQuote)}
            />
            <PricingCard
              name="Listing pack"
              price="From £249"
              cadence="reel + flyer + social bundle"
              features={[
                "Everything in Single reel",
                "Virtual staging on empty rooms",
                "Day → Night exterior reveal",
                "Open-house overlays",
                "Printable A4 PDF flyer + QR code",
                "ElevenLabs voiceover",
              ]}
              cta="Email me"
              onClick={() => (window.location.href = mailtoQuote)}
              featured
            />
            <PricingCard
              name="Brokerage retainer"
              price="Custom"
              cadence="multi-realtor · monthly retainer"
              features={[
                "Unlimited listings, unlimited reels",
                "Per-realtor analytics dashboards",
                "Custom domain on share pages",
                "Priority turnaround",
                "Bulk listing pack export",
              ]}
              cta="Get in touch"
              onClick={() => (window.location.href = mailtoQuote)}
            />
          </div>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────────── */}
      <section ref={ctaRef} className="py-32 px-6 lg:px-12">
        <div className="max-w-[1280px] mx-auto bg-gradient-to-br from-amber-300 via-amber-400 to-amber-500 rounded-3xl p-12 lg:p-20 text-center relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.4), transparent 50%)",
            }}
          />
          <div className="relative">
            <h2 className="font-display text-4xl lg:text-6xl text-black leading-[1.05] mb-6 tracking-[-0.02em]">
              Your next listing.
              <br />
              <span className="opacity-60">Your best reel ever.</span>
            </h2>
            <p className="text-base lg:text-lg text-black/70 max-w-xl mx-auto mb-10">
              Send me a few details about your listing and I'll have a sample reel back to you within 48 hours.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <a
                href={mailtoQuote}
                className="inline-flex items-center gap-2 bg-black text-white font-medium text-base rounded-full px-8 py-3.5 hover:bg-zinc-900 active:scale-[0.97] transition-all"
                data-testid="landing-final-cta"
              >
                <Icons.Mail size={16} />
                Email me about a reel
              </a>
              <a
                href={mailtoDemo}
                className="inline-flex items-center gap-2 text-sm text-black/80 hover:text-black border border-black/20 hover:border-black/50 rounded-full px-6 py-3.5 transition-all"
              >
                See a sample first
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────── */}
      <footer className="border-t border-white/5 py-12 px-6 lg:px-12">
        <div className="max-w-[1280px] mx-auto flex flex-col lg:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center">
              <Icons.Film size={11} className="text-black" />
            </div>
            <div className="text-sm text-zinc-400">
              Property Motion <span className="text-zinc-600">AI</span>
            </div>
          </div>
          <div className="text-xs text-zinc-600 font-mono">
            © {new Date().getFullYear()} · Cinematic listing reels for the modern realtor.
          </div>
          <div className="flex items-center gap-5 text-xs text-zinc-500">
            <a href={mailtoQuote} className="hover:text-white">Contact</a>
            <a href="#features" className="hover:text-white">Features</a>
            <a href="#pricing" className="hover:text-white">Pricing</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ── Sub-components ──────────────────────────────────────────────── */

function Stat({ value, label }) {
  return (
    <div className="flex flex-col items-center lg:items-start">
      <div className="text-2xl font-display text-white">{value}</div>
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-600 mt-0.5">
        {label}
      </div>
    </div>
  );
}

function SectionHeader({ eyebrow, title, sub }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-4">{eyebrow}</div>
      <h2 className="font-display text-4xl lg:text-5xl leading-[1.05] tracking-[-0.02em] max-w-3xl">
        {title}
      </h2>
      {sub && (
        <p className="text-lg text-zinc-400 mt-4 max-w-2xl">{sub}</p>
      )}
    </div>
  );
}

function Step({ n, icon, title, body, accent }) {
  const I = Icons[icon] || Icons.Circle;
  return (
    <div className="group relative bg-white/[0.02] border border-white/10 rounded-2xl p-7 hover:border-white/20 transition-all">
      <div className="absolute top-7 right-7 font-mono text-[11px] text-zinc-600">
        0{n}
      </div>
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center mb-5 shadow-lg`}>
        <I size={20} className="text-black" />
      </div>
      <div className="text-xl font-display mb-3">{title}</div>
      <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
    </div>
  );
}

function Feature({ icon, title, body }) {
  const I = Icons[icon] || Icons.Square;
  return (
    <div className="group bg-white/[0.02] border border-white/10 rounded-2xl p-6 hover:bg-white/[0.04] hover:border-white/20 transition-all">
      <I size={18} className="text-amber-300 mb-4" />
      <div className="text-base font-medium mb-2">{title}</div>
      <p className="text-[13px] text-zinc-400 leading-relaxed">{body}</p>
    </div>
  );
}

function SampleStep({ idx, t, sub, done }) {
  return (
    <div className="flex items-start gap-4 bg-white/[0.02] border border-white/10 rounded-xl p-4">
      <div className="font-mono text-xs text-zinc-600 mt-1">{idx}</div>
      <div className="flex-1">
        <div className="text-sm font-medium">{t}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>
      </div>
      <Icons.CheckCircle2
        size={16}
        className={done ? "text-emerald-400" : "text-zinc-700"}
      />
    </div>
  );
}

function Compare({ line, us }) {
  return (
    <div className="flex items-start gap-3 bg-white/[0.02] border border-white/10 rounded-xl px-4 py-3">
      {us ? (
        <Icons.CheckCircle2 size={16} className="text-amber-300 mt-0.5 shrink-0" />
      ) : (
        <Icons.X size={16} className="text-zinc-700 mt-0.5 shrink-0" />
      )}
      <div className="text-sm text-zinc-300">{line}</div>
    </div>
  );
}

function BeforeAfter({ label, caption, before, after, video, accent, icon }) {
  const I = Icons[icon] || Icons.Square;
  const [showAfter, setShowAfter] = useState(false);
  const videoRef = useRef(null);

  // Auto-toggle every 3.5s — pause on hover so people can study a frame.
  // (still-image mode only; video mode plays its own crossfade)
  const hover = useRef(false);
  useEffect(() => {
    if (video) return undefined; // video mode doesn't need toggle
    const id = setInterval(() => {
      if (!hover.current) setShowAfter((v) => !v);
    }, 3500);
    return () => clearInterval(id);
  }, [video]);

  // Intersection observer — autoplay once visible, pause when off-screen
  // (saves bandwidth on long pages).
  useEffect(() => {
    if (!video || !videoRef.current) return undefined;
    const el = videoRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            el.play().catch(() => {});
          } else {
            el.pause();
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [video]);

  return (
    <div
      className="group bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden hover:border-white/20 transition-all"
      onMouseEnter={() => {
        hover.current = true;
        if (!video) setShowAfter(true);
        if (video && videoRef.current) {
          // Restart the reveal from the beginning so visitors see the full effect
          try {
            videoRef.current.currentTime = 0;
            videoRef.current.play().catch(() => {});
          } catch {
            /* noop */
          }
        }
      }}
      onMouseLeave={() => {
        hover.current = false;
      }}
      data-testid={`landing-ba-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="relative aspect-[16/9] bg-black overflow-hidden">
        {video ? (
          <video
            ref={videoRef}
            src={video}
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            data-testid={`landing-ba-video-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}
          />
        ) : (
          <>
            {/* BEFORE */}
            <img
              src={before}
              alt=""
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
              style={{ opacity: showAfter ? 0 : 1 }}
            />
            {/* AFTER */}
            <img
              src={after}
              alt=""
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
              style={{ opacity: showAfter ? 1 : 0 }}
            />
          </>
        )}
        {/* Gradient bar bottom for label legibility */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

        {/* Stage indicator */}
        <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/70 backdrop-blur-md border border-white/10 rounded-full px-3 py-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${video ? "bg-fuchsia-400 animate-pulse" : showAfter ? "bg-emerald-400" : "bg-zinc-400"}`} />
          <span className="text-[10px] tracking-[0.2em] uppercase text-white font-mono">
            {video ? "Live demo" : showAfter ? "After" : "Before"}
          </span>
        </div>

        {/* Pill label */}
        <div className="absolute top-4 right-4 flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/15 rounded-full px-3 py-1.5">
          <I size={12} className="text-white" />
          <span className="text-[11px] text-white">{label}</span>
        </div>

        {/* Slide divider for visual flair (image mode only) */}
        {!video && (
          <div
            className={`absolute inset-y-0 w-px bg-gradient-to-b ${accent} transition-all duration-700 ease-out`}
            style={{
              left: showAfter ? "100%" : "0%",
              opacity: showAfter ? 0 : 1,
            }}
          />
        )}
      </div>
      <div className="p-5">
        <div className="font-display text-lg mb-1.5">{label}</div>
        <p className="text-[13px] text-zinc-400 leading-relaxed">{caption}</p>
      </div>
    </div>
  );
}

function PricingCard({ name, price, cadence, features, cta, onClick, featured }) {
  return (
    <div
      className={`relative rounded-2xl p-7 transition-all ${
        featured
          ? "bg-white text-black border-2 border-amber-300 shadow-[0_30px_80px_-20px_rgba(251,191,36,0.4)]"
          : "bg-white/[0.02] border border-white/10 hover:border-white/20 text-white"
      }`}
    >
      {featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-300 text-black text-[10px] tracking-[0.3em] uppercase font-bold px-3 py-1 rounded-full">
          Most popular
        </div>
      )}
      <div className={`text-[11px] tracking-[0.3em] uppercase mb-3 ${featured ? "text-zinc-500" : "text-zinc-500"}`}>
        {name}
      </div>
      <div className="flex items-baseline gap-2 mb-1">
        <div className="font-display text-4xl">{price}</div>
      </div>
      <div className={`text-xs ${featured ? "text-zinc-600" : "text-zinc-500"}`}>{cadence}</div>
      <div className={`my-6 h-px ${featured ? "bg-black/10" : "bg-white/10"}`} />
      <ul className="space-y-2.5 mb-7">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2.5">
            <Icons.Check size={14} className={featured ? "text-amber-500 mt-0.5" : "text-amber-300 mt-0.5"} />
            <span className="text-sm">{f}</span>
          </li>
        ))}
      </ul>
      <button
        onClick={onClick}
        className={`w-full text-sm font-medium rounded-full py-3 transition-all ${
          featured
            ? "bg-black text-white hover:bg-zinc-800"
            : "bg-white/10 hover:bg-white/15 text-white border border-white/15"
        }`}
        data-testid={`landing-pricing-${name.toLowerCase()}`}
      >
        {cta}
      </button>
    </div>
  );
}
